import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';

export interface StudyInfo {
	is_done: boolean;
	start_study_time: number;
	end_study_time: number;
}

export interface StudyFilters {
	startDate: Date;
	endDate: Date;
	workingDays: string;
}

@Component({
	selector: 'app-study-filters',
	templateUrl: './study-filters.component.html',
	styleUrls: ['./study-filters.component.css']
})
export class StudyFiltersComponent implements OnInit, OnChanges {
	@Input() studyInfo: StudyInfo = { is_done: false, start_study_time: 0, end_study_time: 0 };
	@Output() filtersChanged = new EventEmitter<StudyFilters>();
	@Output() invalidRangeChange = new EventEmitter<boolean>();
	@Input() forPrint: boolean = false;

	startDate: Date = null;
	endDate: Date = null;
	workingDays: string = 'allDays';

	minStartDate: Date = null;
	maxStartDate: Date = null;
	minEndDate: Date = null;
	maxEndDate: Date = null;

	invalidDateRange: boolean = false;
	studyStartedToday: boolean = false;

	daysFilters = this.siteDashboardService.daysFilters;

	constructor(
		public siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		// this.initFilters();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['studyInfo']) {
			this.initFilters();
		}
	}

	initFilters() {
		if (!this.studyInfo || !this.studyInfo.start_study_time) {
			return;
		}

		const studyStartDate = moment.unix(this.studyInfo.start_study_time).toDate();
		const yesterday = moment().subtract(1, 'day').endOf('day').toDate();
		const todayStart = moment().startOf('day').toDate();

		// Check if study started today
		this.studyStartedToday = moment.unix(this.studyInfo.start_study_time).isSame(moment(), 'day');

		if (this.studyStartedToday) {
			return;
		}

		this.minStartDate = studyStartDate;
		this.minEndDate = studyStartDate;

		if (this.studyInfo.is_done && this.studyInfo.end_study_time) {
			// Completed study: end date capped at study end date
			const studyEndDate = moment.unix(this.studyInfo.end_study_time).toDate();
			this.maxStartDate = studyEndDate;
			this.maxEndDate = studyEndDate;
			this.endDate = studyEndDate;
		} else {
			// Ongoing study: end date capped at yesterday
			this.maxStartDate = yesterday;
			this.maxEndDate = yesterday;
			this.endDate = yesterday;
		}

		this.startDate = studyStartDate;
		this.workingDays = 'allDays';
		this.invalidDateRange = false;

		this.emitFilters();
	}

	onDatesChanged() {
		this.validateDateRange();
		this.invalidRangeChange.emit(this.invalidDateRange);
		if (!this.invalidDateRange) {
			this.emitFilters();
		}
	}

	onWorkingDaysChanged() {
		this.emitFilters();
	}

	validateDateRange() {
		if (!this.startDate || !this.endDate) {
			this.invalidDateRange = false;
			return;
		}

		const startMoment = moment(this.startDate);
		const endMoment = moment(this.endDate);

		// start must not exceed end
		const isStartAfterEnd = startMoment.isAfter(endMoment);

		// start must not be before the study start date
		const isStartBeforeStudyStart = this.minStartDate && startMoment.isBefore(moment(this.minStartDate).startOf('day'));

		// end must not exceed study end date (completed) or yesterday (ongoing)
		const isEndAfterMax = this.maxEndDate && endMoment.isAfter(moment(this.maxEndDate).endOf('day'));

		this.invalidDateRange = isStartAfterEnd || !!isStartBeforeStudyStart || !!isEndAfterMax;
	}

	emitFilters() {
		if (this.invalidDateRange) {
			return;
		}

		this.filtersChanged.emit({
			startDate: this.startDate,
			endDate: this.endDate,
			workingDays: this.workingDays,
		});
	}
}
